import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Monitor as MonitorIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { API_BASE_URL } from '../../config';

const COLORES = [
  '#5c6bc0', '#ec407a', '#29b6f6', '#26a69a',
  '#ab47bc', '#ff7043', '#66bb6a', '#ffa726'
];

function DashboardAusentismo() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para datos
  const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
  const [predicciones, setPredicciones] = useState(null);
  const [reporteMensual, setReporteMensual] = useState(null);
  const [analisisAvanzado, setAnalisisAvanzado] = useState(null);

  // Filtros
  const [periodoFiltro, setPeriodoFiltro] = useState('');
  const [mesesHistorial, setMesesHistorial] = useState(6);
  const [legajoSeleccionado, setLegajoSeleccionado] = useState('');

  useEffect(() => {
    if (tabValue === 0) {
      cargarEstadisticasGenerales();
    } else if (tabValue === 1) {
      cargarPredicciones();
    } else if (tabValue === 2) {
      cargarReporteMensual();
    } else if (tabValue === 3) {
      // Cargar predicciones para el selector si no están cargadas
      if (!predicciones) {
        cargarPredicciones();
      }
      // Cargar análisis si hay legajo seleccionado
      if (legajoSeleccionado) {
        cargarAnalisisAvanzado();
      }
    }
  }, [tabValue, periodoFiltro, mesesHistorial, legajoSeleccionado]);

  const cargarEstadisticasGenerales = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (periodoFiltro) params.append('periodo', periodoFiltro);

      const res = await fetch(`${API_BASE_URL}/api/ausentismo/estadisticas-generales?${params}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Error al cargar estadísticas');
      const data = await res.json();
      setEstadisticasGenerales(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarPredicciones = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/ausentismo/prediccion?meses_historial=${mesesHistorial}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Error al cargar predicciones');
      const data = await res.json();
      setPredicciones(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarReporteMensual = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = periodoFiltro ? `?periodo=${periodoFiltro}` : '';
      const res = await fetch(`${API_BASE_URL}/api/ausentismo/reporte-mensual${params}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Error al cargar reporte');
      const data = await res.json();
      setReporteMensual(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarAnalisisAvanzado = async () => {
    if (!legajoSeleccionado) return;

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/ausentismo/analisis-avanzado/${legajoSeleccionado}?meses=${mesesHistorial}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Error al cargar análisis avanzado');
      const data = await res.json();
      setAnalisisAvanzado(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getNivelRiesgoColor = (nivel) => {
    switch (nivel) {
      case 'alto': return 'error';
      case 'medio': return 'warning';
      case 'bajo': return 'success';
      default: return 'default';
    }
  };

  const generarPeriodos = () => {
    const periodos = [];
    const hoy = new Date();
    for (let i = 0; i < 12; i++) {
      const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      periodos.push(fecha.toISOString().slice(0, 7).replace('-', ''));
    }
    return periodos;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          📊 Dashboard de Ausentismo
        </Typography>
        <IconButton onClick={() => {
          if (tabValue === 0) cargarEstadisticasGenerales();
          else if (tabValue === 1) cargarPredicciones();
          else cargarReporteMensual();
        }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Estadísticas Generales" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Predicciones (Poisson)" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Reporte Mensual" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Análisis Avanzado" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB 0: ESTADÍSTICAS GENERALES */}
      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={periodoFiltro}
                label="Período"
                onChange={(e) => setPeriodoFiltro(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {generarPeriodos().map(p => (
                  <MenuItem key={p} value={p}>
                    {`${p.substring(0, 4)}-${p.substring(4)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : estadisticasGenerales && (
            <Grid container spacing={3}>
              {/* Ausencias por Concepto */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Ausencias por Concepto
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={estadisticasGenerales.por_concepto}
                          dataKey="total_eventos"
                          nameKey="concepto"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {estadisticasGenerales.por_concepto.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORES[index % COLORES.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tendencia Mensual */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Tendencia Mensual
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={estadisticasGenerales.tendencia_mensual.slice().reverse()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="periodo" tickFormatter={(val) => `${val.substring(0, 4)}-${val.substring(4)}`} />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="total_eventos" stroke="#5c6bc0" name="Total Eventos" />
                        <Line type="monotone" dataKey="dias_totales" stroke="#ec407a" name="Días Totales" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top 10 Empleados con Más Ausencias */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top 10 Empleados con Más Ausencias
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Legajo</TableCell>
                          <TableCell>Empleado</TableCell>
                          <TableCell align="center">Total Ausencias</TableCell>
                          <TableCell align="center">Días Totales</TableCell>
                          <TableCell>Conceptos</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {estadisticasGenerales.top_empleados.map((emp) => (
                          <TableRow key={emp.legajo}>
                            <TableCell>{emp.legajo}</TableCell>
                            <TableCell>{emp.empleado_nombre}</TableCell>
                            <TableCell align="center">
                              <Chip label={emp.total_ausencias} color="primary" size="small" />
                            </TableCell>
                            <TableCell align="center">{emp.dias_totales}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {emp.conceptos}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              {/* Detalle por Concepto */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Detalle por Tipo de Ausencia
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={estadisticasGenerales.por_concepto}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="concepto" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="total_eventos" fill="#5c6bc0" name="Total Eventos" />
                        <Bar dataKey="empleados_afectados" fill="#29b6f6" name="Empleados Afectados" />
                        <Bar dataKey="dias_totales" fill="#ec407a" name="Días Totales" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* TAB 1: PREDICCIONES */}
      {tabValue === 1 && (
        <>
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Meses de Historial</InputLabel>
              <Select
                value={mesesHistorial}
                label="Meses de Historial"
                onChange={(e) => setMesesHistorial(e.target.value)}
              >
                <MenuItem value={3}>3 meses</MenuItem>
                <MenuItem value={6}>6 meses</MenuItem>
                <MenuItem value={12}>12 meses</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : predicciones && (
            <Grid container spacing={3}>
              {/* Resumen de Riesgos */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ffebee' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="error" />
                      <Typography variant="h4" fontWeight="600">
                        {predicciones.riesgo_alto}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Riesgo Alto
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#fff8e1' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="warning" />
                      <Typography variant="h4" fontWeight="600">
                        {predicciones.riesgo_medio}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Riesgo Medio
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#e8f5e9' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="success" />
                      <Typography variant="h4" fontWeight="600">
                        {predicciones.riesgo_bajo}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Riesgo Bajo
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon color="info" />
                      <Typography variant="h4" fontWeight="600">
                        {predicciones.total_empleados_analizados}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Analizados
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tabla de Predicciones */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Predicción de Ausencias por Empleado
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {predicciones.periodo_analizado}
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Legajo</TableCell>
                          <TableCell>Empleado</TableCell>
                          <TableCell align="center">Riesgo</TableCell>
                          <TableCell align="center">λ (Lambda)</TableCell>
                          <TableCell align="center">Esperadas</TableCell>
                          <TableCell align="center">IC 95%</TableCell>
                          <TableCell align="center">P(0)</TableCell>
                          <TableCell align="center">P(1-2)</TableCell>
                          <TableCell align="center">P(≥3)</TableCell>
                          <TableCell>Conceptos</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {predicciones.predicciones.map((pred) => (
                          <TableRow key={pred.legajo}>
                            <TableCell>{pred.legajo}</TableCell>
                            <TableCell>{pred.nombre}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={pred.nivel_riesgo.toUpperCase()}
                                color={getNivelRiesgoColor(pred.nivel_riesgo)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Tasa promedio (Poisson)">
                                <Typography variant="body2">{pred.lambda}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={pred.ausencias_esperadas_proximo_mes} color="primary" size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Intervalo de confianza 95%">
                                <Typography variant="caption">
                                  [{pred.intervalo_confianza.inferior} - {pred.intervalo_confianza.superior}]
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={pred.probabilidades.sin_ausencias}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={pred.probabilidades.ausencias_1_2}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={pred.probabilidades.ausencias_3_mas}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {pred.conceptos_frecuentes.slice(0, 3).map((c, i) => (
                                  <Chip key={i} label={c} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* TAB 2: REPORTE MENSUAL */}
      {tabValue === 2 && (
        <>
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={periodoFiltro}
                label="Período"
                onChange={(e) => setPeriodoFiltro(e.target.value)}
              >
                {generarPeriodos().map(p => (
                  <MenuItem key={p} value={p}>
                    {`${p.substring(0, 4)}-${p.substring(4)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : reporteMensual && (
            <Grid container spacing={3}>
              {/* Resumen */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Resumen del Período {reporteMensual.periodo.substring(0, 4)}-{reporteMensual.periodo.substring(4)}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Empleados con Ausencias
                          </Typography>
                          <Typography variant="h4">
                            {reporteMensual.total_empleados_con_ausencias}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Eventos
                          </Typography>
                          <Typography variant="h4">
                            {reporteMensual.total_eventos}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Días
                          </Typography>
                          <Typography variant="h4">
                            {reporteMensual.total_dias}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Detalle por Empleado */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Detalle por Empleado
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Legajo</TableCell>
                          <TableCell>Empleado</TableCell>
                          <TableCell align="center">Total Eventos</TableCell>
                          <TableCell align="center">Total Días</TableCell>
                          <TableCell>Detalle de Ausencias</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reporteMensual.detalle.map((emp) => (
                          <TableRow key={emp.legajo}>
                            <TableCell>{emp.legajo}</TableCell>
                            <TableCell>{emp.nombre}</TableCell>
                            <TableCell align="center">
                              <Chip label={emp.total_eventos} color="primary" size="small" />
                            </TableCell>
                            <TableCell align="center">{emp.total_dias}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {emp.ausencias.map((aus, i) => (
                                  <Tooltip key={i} title={`${aus.dias} días`}>
                                    <Chip
                                      label={`${aus.concepto} (${aus.cantidad})`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* TAB 3: ANÁLISIS AVANZADO */}
      {tabValue === 3 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Legajo del Empleado</InputLabel>
              <Select
                value={legajoSeleccionado}
                label="Legajo del Empleado"
                onChange={(e) => setLegajoSeleccionado(e.target.value)}
              >
                <MenuItem value="">Seleccionar...</MenuItem>
                {predicciones?.predicciones?.map(p => (
                  <MenuItem key={p.legajo} value={p.legajo}>
                    {p.legajo} - {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Meses de Historial</InputLabel>
              <Select
                value={mesesHistorial}
                label="Meses de Historial"
                onChange={(e) => setMesesHistorial(e.target.value)}
              >
                <MenuItem value={3}>3 meses</MenuItem>
                <MenuItem value={6}>6 meses</MenuItem>
                <MenuItem value={12}>12 meses</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {!legajoSeleccionado ? (
            <Alert severity="info">
              Seleccione un empleado para ver el análisis estadístico completo
            </Alert>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : analisisAvanzado && analisisAvanzado.analisis ? (
            <Grid container spacing={3}>
              {/* Resumen Principal */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      📊 Resumen Estadístico - Legajo {analisisAvanzado.legajo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {analisisAvanzado.periodo_analizado} ({analisisAvanzado.datos_analizados} registros)
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Ausencias Esperadas
                          </Typography>
                          <Typography variant="h4">
                            {analisisAvanzado.analisis.resumen.ausencias_esperadas}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Nivel de Riesgo
                          </Typography>
                          <Chip
                            label={analisisAvanzado.analisis.resumen.nivel_confianza.toUpperCase()}
                            color={getNivelRiesgoColor(analisisAvanzado.analisis.resumen.nivel_confianza)}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Tendencia
                          </Typography>
                          <Typography variant="h6">
                            {analisisAvanzado.analisis.resumen.tendencia}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Prob. Próximo Mes
                          </Typography>
                          <Typography variant="h6">
                            {(parseFloat(analisisAvanzado.analisis.resumen.probabilidad_proximo_mes) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Modelo de Poisson */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssessmentIcon color="primary" />
                      Distribución de Poisson
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>λ (Lambda - Tasa promedio)</TableCell>
                          <TableCell align="right">
                            <Chip label={analisisAvanzado.analisis.modelo_poisson.lambda.toFixed(3)} color="primary" size="small" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Valor Esperado</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.modelo_poisson.esperado.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Moda (Valor más probable)</TableCell>
                          <TableCell align="right">
                            <strong>{analisisAvanzado.analisis.modelo_poisson.moda}</strong>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>IC 95% Inferior</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.modelo_poisson.intervalo_confianza_95.lower.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>IC 95% Superior</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.modelo_poisson.intervalo_confianza_95.upper.toFixed(2)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight="600" gutterBottom>
                        Probabilidades:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`P(0) = ${(analisisAvanzado.analisis.modelo_poisson.probabilidades.sin_ausencias * 100).toFixed(1)}%`}
                          size="small"
                          color="success"
                        />
                        <Chip
                          label={`P(1-2) = ${(analisisAvanzado.analisis.modelo_poisson.probabilidades.ausencias_1_2 * 100).toFixed(1)}%`}
                          size="small"
                          color="info"
                        />
                        <Chip
                          label={`P(≥3) = ${(analisisAvanzado.analisis.modelo_poisson.probabilidades.ausencias_3_mas * 100).toFixed(1)}%`}
                          size="small"
                          color="error"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Regresión de Poisson */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon color="primary" />
                      Regresión de Poisson
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>λ Base</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.regresion.lambda_base.toFixed(3)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>λ Ajustado</TableCell>
                          <TableCell align="right">
                            <Chip label={analisisAvanzado.analisis.regresion.lambda_ajustado.toFixed(3)} color="secondary" size="small" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2" fontWeight="600" sx={{ mt: 1, mb: 1 }}>
                              Factores Aplicados:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {Object.entries(analisisAvanzado.analisis.regresion.factores_aplicados).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value === true ? 'Sí' : value === false ? 'No' : value}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              {/* Serie Temporal */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssessmentIcon color="primary" />
                      Análisis de Serie Temporal
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Tendencia</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={analisisAvanzado.analisis.serie_temporal.tendencia}
                              color={analisisAvanzado.analisis.serie_temporal.tendencia === 'creciente' ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Pendiente</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.serie_temporal.pendiente}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Intercepto</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.serie_temporal.intercepto}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Estacionalidad</TableCell>
                          <TableCell align="right">
                            {analisisAvanzado.analisis.serie_temporal.estacionalidad ? 'Detectada' : 'No detectada'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Diferencia Estacional</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.serie_temporal.diferencia_estacional}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Predicción Próximo</TableCell>
                          <TableCell align="right">
                            <strong>{analisisAvanzado.analisis.serie_temporal.prediccion_proximo.toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              {/* Análisis de Supervivencia */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonitorIcon color="primary" />
                      Análisis de Supervivencia
                    </Typography>
                    {analisisAvanzado.analisis.supervivencia.tiempo_medio_entre_ausencias ? (
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Tiempo Medio Entre Ausencias</TableCell>
                            <TableCell align="right">
                              <Chip label={`${analisisAvanzado.analisis.supervivencia.tiempo_medio_entre_ausencias} días`} color="primary" size="small" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Desviación Estándar</TableCell>
                            <TableCell align="right">{analisisAvanzado.analisis.supervivencia.desviacion_estandar} días</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Días desde Última Ausencia</TableCell>
                            <TableCell align="right">{analisisAvanzado.analisis.supervivencia.dias_desde_ultima_ausencia} días</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Tasa λ (Exponencial)</TableCell>
                            <TableCell align="right">{analisisAvanzado.analisis.supervivencia.tasa_lambda}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>P(Ausencia en 30 días)</TableCell>
                            <TableCell align="right">
                              <strong>{(parseFloat(analisisAvanzado.analisis.supervivencia.probabilidad_ausencia_proximo_mes) * 100).toFixed(1)}%</strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Alert severity="info">Datos insuficientes para análisis de supervivencia</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Suavizado Exponencial */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon color="primary" />
                      Suavizado Exponencial
                    </Typography>
                    {analisisAvanzado.analisis.suavizado_exponencial ? (
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Factor α (Alpha)</TableCell>
                            <TableCell align="right">{analisisAvanzado.analisis.suavizado_exponencial.alpha}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Predicción Siguiente Período</TableCell>
                            <TableCell align="right">
                              <Chip label={analisisAvanzado.analisis.suavizado_exponencial.prediccion_siguiente.toFixed(2)} color="primary" size="small" />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Alert severity="info">Sin datos para suavizado exponencial</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Detección de Outliers */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="warning" />
                      Detección de Outliers (IQR)
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Q1 (Cuartil 1)</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.outliers.q1}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Q3 (Cuartil 3)</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.outliers.q3}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>IQR (Rango Intercuartílico)</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.outliers.iqr.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Límite Inferior</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.outliers.limite_inferior.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Límite Superior</TableCell>
                          <TableCell align="right">{analisisAvanzado.analisis.outliers.limite_superior.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Outliers Detectados</TableCell>
                          <TableCell align="right">
                            {analisisAvanzado.analisis.outliers.outliers.length > 0 ? (
                              <Chip
                                label={`${analisisAvanzado.analisis.outliers.outliers.length} valores`}
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Chip label="Ninguno" color="success" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                        {analisisAvanzado.analisis.outliers.outliers.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                {analisisAvanzado.analisis.outliers.outliers.map((val, i) => (
                                  <Chip key={i} label={val} size="small" variant="outlined" color="warning" />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : analisisAvanzado && !analisisAvanzado.analisis ? (
            <Alert severity="warning">{analisisAvanzado.mensaje}</Alert>
          ) : null}
        </>
      )}
    </Container>
  );
}

export default DashboardAusentismo;
